import React, { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@accure/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { SendHorizonal } from 'lucide-react';
import { useBoolean } from 'usehooks-ts';
import { z } from 'zod';

import { TextareaInput } from '@/components/form/inputs/textarea-input';
import type { TaskComment } from '@/task-manager/entities/task-comment';

const taskCardUpsertForm = z.object({
  content: z.string().min(1),
});
export type TaskCommentUpsertForm = z.infer<typeof taskCardUpsertForm>;

export type CommentFormProps = {
  comment?: TaskComment;
  onSubmit: (data: TaskCommentUpsertForm) => Promise<void>;
  onClose?: () => void;
};

export function CommentForm({ comment, onSubmit, onClose }: CommentFormProps) {
  const submitRef = useRef<HTMLButtonElement | null>(null);
  const isLoading = useBoolean(false);
  const form = useForm<TaskCommentUpsertForm>({
    defaultValues: {
      content: comment?.content ?? '',
    },
    resolver: zodResolver(taskCardUpsertForm),
  });

  const { isValid } = form.formState;

  async function submitComment(data: TaskCommentUpsertForm) {
    isLoading.setTrue();
    try {
      await onSubmit(data);
      form.reset();
      form.setFocus('content');
    } catch (error) {
      toast.error('Failed to submit comment. Please try again.');
      throw error;
    } finally {
      isLoading.setFalse();
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Escape') {
      onClose?.();
      event.stopPropagation();
    } else if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submitRef.current?.click();
      event.stopPropagation();
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(submitComment)}
        className="flex flex-col gap-4"
      >
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <TextareaInput
                  {...field}
                  rows={3}
                  placeholder="Add your comment..."
                  onKeyDown={handleKeyDown}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end gap-2">
          {comment ? (
            <>
              <Button
                onClick={onClose}
                type="button"
                className="border-zinc-400 py-2 text-sm text-zinc-600 hover:bg-zinc-600/5"
                variant="ghost"
              >
                Cancel
              </Button>
              <Button
                ref={submitRef}
                type="submit"
                variant="outline"
                className="py-2 text-sm"
                disabled={!isValid || isLoading.value}
              >
                Save
              </Button>
            </>
          ) : (
            <Button
              ref={submitRef}
              type="submit"
              variant="outline"
              className="py-2 pl-6 text-sm"
              disabled={!isValid || isLoading.value}
            >
              <SendHorizonal />
              Comment
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
}
